import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { userPreferencesService } from '@/lib/services/userPreferences';
import { useDeviceInfo } from '@/utils/deviceDetection';
import { useIOSKeyboardDetection } from '@/hooks/useIOSKeyboardDetection';
import { settingsService } from '@/lib/services/settings';
import { isEquipmentPageOn } from '@/lib/equipmentPages';
import { hasKitchenAccess } from '@/utils/departmentAccess';
import {
  ClipboardList,
  GraduationCap,
  CheckSquare,
  FileText,
  Calendar,
  LayoutDashboard,
  Users,
  TrendingUp,
  BarChart,
  UserCheck,
  Star,
  Sparkles,
  Trash2,
  Wrench,
  MessageCircle,
  UserPlus,
  Vault
} from '@/components/ui/icons';
import {
  ChefHat,
  CalendarDays,
  HelpCircle,
  ShieldCheck,
  Building2,
  Gift,
  Settings,
  Link as LinkIcon,
  ArrowLeftRight,
  Brush,
  ClipboardCheck,
  ClipboardPen
} from 'lucide-react';
import { useShiftReportsAccess } from '@/hooks/useShiftReportsAccess';
import { useRewardsCurrency } from '@/hooks/useRewardsCurrency';
import { applyCurrencyName } from '@/lib/rewardsCurrency';

// Helper functions for level-based position checks (backwards compatible)
const getUserPositionLevel = (user: any): number => {
  if (!user) return 1;
  if ((user as any).positionLevel) return (user as any).positionLevel;

  // Fallback to name matching for backwards compatibility
  const pos = ((user as any).position || '').toLowerCase();
  if (pos === 'owner operator' || pos.includes('director') || pos.includes('administrator')) return 4;
  if (pos.includes('leader')) return 3;
  if (pos === 'trainer' || pos.includes('trainer')) return 2;
  return 1;
};

const hasDirectorLevelAccess = (user: any): boolean => getUserPositionLevel(user) >= 4;
const hasLeaderLevelAccess = (user: any): boolean => getUserPositionLevel(user) >= 3;
const hasTrainerLevelAccess = (user: any): boolean => getUserPositionLevel(user) >= 2;
const isTeamMemberLevel = (user: any): boolean => getUserPositionLevel(user) === 1;

// Define all available navigation items with their configurations
const ALL_NAV_ITEMS = {
  dashboard: {
    icon: LayoutDashboard,
    label: 'Home', // Changed from 'Dashboard' - shorter and more intuitive
    href: '/dashboard',
    defaultShow: true
  },
  foh: {
    icon: CheckSquare,
    label: 'FOH', // Changed from 'FOH Tasks' - simpler
    href: '/foh',
    defaultShow: true,
    requiresFeature: 'fohTasks'
  },
  cleaning: {
    icon: Sparkles,
    label: 'Cleaning',
    href: '/foh/cleaning',
    defaultShow: false
  },
  fohEquipment: {
    icon: Wrench,
    label: 'Equipment',
    href: '/foh/equipment',
    defaultShow: false,
    requiresFeature: 'fohTasks'
  },
  // Director Board — also needs the store switch (Settings → Directors); checked in the loops below
  directors: {
    icon: Star,
    label: 'Directors',
    href: '/foh/directors',
    showIf: (user: any) => hasDirectorLevelAccess(user) || user?.role === 'admin',
    defaultShow: false
  },
  // Shift Reports add-on — gated by /me (on AND filer-or-admin); checked in the loops below
  shiftReports: {
    icon: ClipboardPen,
    label: 'Reports',
    href: '/shift-reports',
    defaultShow: false
  },
  // ServSafe add-on — per-store switch, checked in the loops below
  servSafe: {
    icon: ShieldCheck,
    label: 'ServSafe',
    href: '/servsafe',
    defaultShow: false
  },
  documentation: {
    icon: FileText,
    getLabel: (user: any, _t: any) => hasLeaderLevelAccess(user) ? 'Docs' : 'My Docs',
    href: (user: any) => hasLeaderLevelAccess(user) ? '/documentation' : `/documentation?employee=${(user as any)?._id}`,
    showIf: (_user: any) => true,
    defaultShow: true,
    requiresFeature: 'documentation'
  },
  kitchen: {
    icon: ChefHat,
    getLabel: (user: any, t: any) => t('navigation.kitchen'), // t is used
    href: '/kitchen',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: true,
    requiresFeature: 'kitchen'
  },
  wasteTracker: {
    icon: Trash2,
    label: 'Waste', // Changed from 'Waste Tracker'
    href: '/kitchen/waste-tracker',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  kitchenEquipment: {
    icon: Wrench,
    label: 'Equipment',
    href: '/kitchen/equipment',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  foodSafety: {
    icon: ShieldCheck,
    label: 'Safety', // Changed from 'Food Safety'
    href: '/kitchen/food-safety',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  bohCleaning: {
    icon: Brush,
    label: 'BOH Cleaning',
    href: '/kitchen/cleaning',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  kitchenChecklists: {
    icon: ClipboardList,
    label: 'Checklists',
    href: '/kitchen/checklists',
    showIf: (user: any) => hasKitchenAccess(user) || hasLeaderLevelAccess(user),
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  training: {
    icon: GraduationCap,
    getLabel: (user: any, t: any) => t('navigation.training'),
    href: '/training',
    defaultShow: true,
    requiresFeature: 'training'
  },
  setupSheet: {
    icon: CalendarDays,
    label: 'Setup', // Changed from 'Setup Sheet'
    href: '/saved-setups',
    defaultShow: true,
    requiresFeature: 'setups'
  },
  evaluations: {
    icon: ClipboardList,
    label: 'Evals', // Changed from 'Evaluations'
    href: '/evaluations',
    showIf: (user: any) => hasLeaderLevelAccess(user), // Level 3+ only
    defaultShow: false,
    requiresFeature: 'evaluations'
  },
  leadership: {
    icon: TrendingUp,
    label: 'Leader', // Changed from 'Leadership'
    href: '/leadership',
    showIf: (user: any) => user?.position !== 'Team Member',
    defaultShow: false,
    requiresFeature: 'leadership'
  },
  analytics: {
    icon: BarChart,
    label: 'Analytics',
    href: '/analytics',
    showIf: (user: any) => user?.position !== 'Team Member',
    defaultShow: false
  },
  users: {
    icon: Users,
    getLabel: (user: any, _t: any) => user?.position === 'Team Member' ? 'Profile' : 'Team', // Changed from 'My Profile'/'Team Members'
    href: (user: any) => user?.position === 'Team Member' ? `/users/${user?._id}` : '/users',
    defaultShow: false
  },

  teamSurveys: {
    icon: ClipboardList,
    label: 'Surveys', // Changed from 'Team Surveys'
    href: '/team-surveys',
    showIf: (user: any) => hasLeaderLevelAccess(user), // Leaders (3+) send surveys; the API refuses Trainers
    defaultShow: false,
    requiresFeature: 'leadership'
  },
  teamDevelopment: {
    icon: TrendingUp,
    getLabel: (user: any, _t: any) => isTeamMemberLevel(user) ? 'Growth' : 'Development', // Changed from 'My Development'/'Team Development'
    href: (user: any) => isTeamMemberLevel(user) ? '/team-development/my-plans' : '/team-development/overview',
    showIf: (user: any) => {
      // Hide for Trainers with leadership-only access (they don't have backend access)
      if (hasTrainerLevelAccess(user) && !hasLeaderLevelAccess(user) && (user as any)?.accessLevel === 'leadership-only') {
        return false;
      }
      return true;
    },
    defaultShow: true,
    requiresFeature: 'leadership'
  },
  howTo: {
    icon: HelpCircle,
    label: 'How To',
    href: '/how-to',
    defaultShow: false
  },
  assessments: {
    icon: UserCheck,
    label: 'Assessments',
    href: '/leadership/assessments',
    showIf: (user: any) => hasTrainerLevelAccess(user), // Trainer level and above
    defaultShow: false,
    requiresFeature: 'leadership'
  },
  evaluations360: {
    icon: Star,
    label: '360 Reviews',
    href: '/leadership/360-evaluations',
    showIf: (user: any) => hasTrainerLevelAccess(user), // Trainer level and above
    defaultShow: false,
    requiresFeature: 'leadership'
  },
  vendors: {
    icon: Building2,
    label: 'Vendors',
    href: '/vendors',
    showIf: (user: any) => hasDirectorLevelAccess(user), // Director level only
    defaultShow: false
  },
  teamChat: {
    icon: MessageCircle,
    label: 'Team Chat',
    href: '/chat',
    defaultShow: false
  },
  calendar: {
    icon: Calendar,
    label: 'Calendar',
    href: '/calendar',
    defaultShow: false,
    requiresFeature: 'leadership'
  },
  guestRecovery: {
    icon: UserPlus,
    label: 'Guest Recovery',
    href: '/customer-complaints',
    showIf: (user: any) => hasLeaderLevelAccess(user), // Leader level and above
    defaultShow: false
  },
  rewards: {
    icon: Gift,
    label: 'CFAdollars',
    href: '/rewards',
    defaultShow: false
  },
  safeCounting: {
    icon: Vault,
    label: 'Safe',
    href: '/safe-counting',
    showIf: (user: any) => hasLeaderLevelAccess(user) || (user as any)?.isAdmin, // Leader level and above
    defaultShow: false,
    requiresFeature: 'safeCounting'
  },
  quickLinks: {
    icon: LinkIcon,
    label: 'Quick Links',
    href: '/quick-links',
    defaultShow: false
  },
  borrowLog: {
    icon: ArrowLeftRight,
    label: 'Borrow',
    href: '/borrow-log',
    showIf: (user: any) => hasLeaderLevelAccess(user) || (user as any)?.isAdmin,
    // Off by default: the store toggle decides whether it is available at all,
    // and a person who wants it in their bottom five adds it themselves.
    defaultShow: false
  },
  selfAssessments: {
    icon: ClipboardCheck,
    label: 'Assess',
    href: '/kitchen/self-assessments',
    showIf: () => true,
    // Off by default: the store toggle decides availability; a person who wants
    // it in their bottom five adds it themselves.
    defaultShow: false,
    requiresFeature: 'kitchen'
  },
  operatorOverview: {
    icon: Building2,
    label: 'Digest', // "Weekly Digest" in main nav
    href: '/operator-overview',
    showIf: (user: any) => hasDirectorLevelAccess(user) || (user as any)?.isAdmin, // Director/Admin only
    defaultShow: false
  },
  settings: {
    icon: Settings,
    label: 'Settings',
    href: '/settings',
    defaultShow: false
  }
};

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currency = useRewardsCurrency();
  const { t } = useTranslation();
  const { isFeatureEnabled } = useSubscription();
  const [navItems, setNavItems] = useState<any[]>([]);

  // Initialize iOS keyboard detection
  const { isIOS } = useIOSKeyboardDetection();

  // Use unified device detection
  const deviceInfo = useDeviceInfo();

  // Fetch user preferences to get navigation preferences
  const { data: userPreferences } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: userPreferencesService.getUserPreferences,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true // Refetch when window regains focus
  });
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: settingsService.getSettings })
  const { showNav: showShiftReports } = useShiftReportsAccess()

  // Process navigation items based on user preferences
  useEffect(() => {
    // Get navigation preferences from user preferences
    const userNavPreferences = userPreferences?.uiPreferences?.mobileNavigation?.items;

    // If user has custom navigation preferences, use those
    if (userNavPreferences && userNavPreferences.length > 0) {

      const processedItems = userNavPreferences
        .map(item => {
          const navConfig = ALL_NAV_ITEMS[item.key];
          if (!navConfig) return null;

          // Check if this item should be shown based on user role/department and subscription
          let shouldShow = item.show;
          if (navConfig.showIf && typeof navConfig.showIf === 'function') {
            shouldShow = shouldShow && navConfig.showIf(user);
          }

          // Check if this item requires a specific feature
          if (navConfig.requiresFeature) {
            shouldShow = shouldShow && isFeatureEnabled(navConfig.requiresFeature);
          }
          // Additionally hide Team Development for Team Members/Trainers when Promotion Pathways is disabled
          if (item.key === 'teamDevelopment') {
            const isTmOrTrainer = !hasLeaderLevelAccess(user)
            if (isTmOrTrainer && !isFeatureEnabled('promotionPathways')) {
              shouldShow = false;
            }
          }

          // Hide Setup nav for non-leadership when leadersOnly is enabled
          if (item.key === 'setupSheet') {
            const leadersOnly = !!settings?.userAccess?.setupView?.leadersOnly
            const isLeaderOrAbove = hasLeaderLevelAccess(user) || user?.role === 'admin'
            if (leadersOnly && !isLeaderOrAbove) {
              shouldShow = false
            }
          }

          // Hide Rewards when feature is disabled
          if (item.key === 'rewards') {
            if (!settings?.rewardsConfig?.enabled) {
              shouldShow = false
            }
          }

          // Hide Safe Counting when feature is disabled
          if (item.key === 'safeCounting') {
            if (!settings?.safeConfig?.enabled) {
              shouldShow = false
            }
          }

          // Per-store setting, not a subscription feature.
          if (item.key === 'borrowLog' && !settings?.userAccess?.borrowLog?.enabled) {
            shouldShow = false;
          }

          // Per-store setting, not a subscription feature.
          if (item.key === 'selfAssessments' && !settings?.userAccess?.selfAssessments?.enabled) {
            shouldShow = false;
          }

          if (item.key === 'servSafe' && !settings?.userAccess?.servSafe?.enabled) {
            shouldShow = false;
          }

          // Store admins can hide the equipment pages (Settings → Kitchen / FOH).
          if (item.key === 'kitchenEquipment' && !isEquipmentPageOn(settings, 'kitchen')) {
            shouldShow = false;
          }
          if (item.key === 'fohEquipment' && !isEquipmentPageOn(settings, 'foh')) {
            shouldShow = false;
          }
          if (item.key === 'directors' && !settings?.directorBoard?.enabled) {
            shouldShow = false;
          }
          if (item.key === 'shiftReports' && !showShiftReports) {
            shouldShow = false;
          }

          // Get the label (static or dynamic)
          let label = navConfig.label;
          if (navConfig.getLabel && typeof navConfig.getLabel === 'function') {
            label = navConfig.getLabel(user, t);
          }

          // Get the href (static or dynamic)
          let href = navConfig.href;
          if (typeof href === 'function') {
            href = href(user);
          }

          return {
            icon: navConfig.icon,
            label,
            href,
            show: shouldShow
          };
        })
        .filter(Boolean); // Remove null items

      setNavItems(processedItems);
    } else {
      // Use default navigation items if no custom preferences
      const defaultItems = [
        { key: 'dashboard', show: true },
        { key: 'foh', show: true },
        { key: 'documentation', show: true },
        { key: 'evaluations', show: true },
        { key: 'teamDevelopment', show: true }
      ];

      const processedItems = defaultItems
        .map(item => {
          const navConfig = ALL_NAV_ITEMS[item.key];
          if (!navConfig) return null;

          // Check if this item should be shown based on user role/department and subscription
          let shouldShow = item.show;
          if (navConfig.showIf && typeof navConfig.showIf === 'function') {
            shouldShow = shouldShow && navConfig.showIf(user);
          }

          // Check if this item requires a specific feature
          if (navConfig.requiresFeature) {
            shouldShow = shouldShow && isFeatureEnabled(navConfig.requiresFeature);
          }
          // Additionally hide Team Development for Team Members/Trainers when Promotion Pathways is disabled
          if (item.key === 'teamDevelopment') {
            const isTmOrTrainer = !hasLeaderLevelAccess(user)
            if (isTmOrTrainer && !isFeatureEnabled('promotionPathways')) {
              shouldShow = false
            }
          }

          // Hide Safe Counting when feature is disabled
          if (item.key === 'safeCounting') {
            if (!settings?.safeConfig?.enabled) {
              shouldShow = false
            }
          }

          // Per-store setting, not a subscription feature.
          if (item.key === 'borrowLog' && !settings?.userAccess?.borrowLog?.enabled) {
            shouldShow = false;
          }

          // Per-store setting, not a subscription feature.
          if (item.key === 'selfAssessments' && !settings?.userAccess?.selfAssessments?.enabled) {
            shouldShow = false;
          }

          if (item.key === 'servSafe' && !settings?.userAccess?.servSafe?.enabled) {
            shouldShow = false;
          }

          // Store admins can hide the equipment pages (Settings → Kitchen / FOH).
          if (item.key === 'kitchenEquipment' && !isEquipmentPageOn(settings, 'kitchen')) {
            shouldShow = false;
          }
          if (item.key === 'fohEquipment' && !isEquipmentPageOn(settings, 'foh')) {
            shouldShow = false;
          }
          if (item.key === 'directors' && !settings?.directorBoard?.enabled) {
            shouldShow = false;
          }
          if (item.key === 'shiftReports' && !showShiftReports) {
            shouldShow = false;
          }

          // Get the label (static or dynamic)
          let label = navConfig.label;
          if (navConfig.getLabel && typeof navConfig.getLabel === 'function') {
            label = navConfig.getLabel(user, t);
          }

          // Get the href (static or dynamic)
          let href = navConfig.href;
          if (typeof href === 'function') {
            href = href(user);
          }

          return {
            icon: navConfig.icon,
            label,
            href,
            show: shouldShow
          };
        })
        .filter(Boolean); // Remove null items

      setNavItems(processedItems);
    }
  }, [userPreferences, user, t, settings, isFeatureEnabled, showShiftReports]);

  // If no items are available or still loading, show a placeholder
  if (navItems.length === 0) {
    return null;
  }

  return (
    <nav
      data-tour="tm-mobile-nav"
      className={cn(
        "mobile-nav",
        deviceInfo.deviceClass && `device-class-${deviceInfo.deviceClass}`,
        isIOS && "ios-device"
      )}
    >
      <div className="mobile-nav-content">
        {/* Bottom navigation items */}
        {navItems
          .filter(item => item.show)
          .map((item) => {
            const Icon = item.icon;
            const isActive = item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

            return (
              <button
                key={item.href}
                onClick={() => navigate(item.href)}
                className={cn(
                  "mobile-nav-button",
                  isActive ? "text-red-600" : "text-gray-500 hover:text-gray-900"
                )}
                aria-label={applyCurrencyName(item.label, currency)}
                type="button"
              >
                <Icon className="flex-shrink-0 mobile-nav-icon" />
                <span className="mobile-nav-button-text">{applyCurrencyName(item.label, currency)}</span>
              </button>
            );
          })}
      </div>
    </nav>
  );
}